
import React, { useMemo } from 'react';
import { X, GitMerge, Sparkles, Rocket, Bug } from 'lucide-react';

interface ChangelogModalProps {
  onClose: () => void;
}

type UpdateType = 'major' | 'minor' | 'patch';

interface LogEntry {
  date: string;
  type: UpdateType;
  title: string;
  description: string;
  features: string[];
}

const RAW_LOGS: LogEntry[] = [
  {
    date: 'Heute',
    type: 'patch',
    title: 'Changelog & UI Polish',
    description: 'Einführung einer Update-Historie und Feinjustierung der Benutzeroberfläche.',
    features: [
      'Neues Changelog-Modal mit dynamischer Versionierung',
      'Optimierung der Header-Leiste'
    ]
  },
  {
    date: 'Gestern',
    type: 'minor',
    title: 'Sortierung & Listen-Logik',
    description: 'Verbesserte Organisation der Einträge im Maßnahmenkatalog.',
    features: [
      'Automatische alphabetische Sortierung innerhalb von Kategorien',
      'Optimierter AI-Prompt für strukturierte Daten',
      'Stabilere Speicherung bei KI-Optimierung'
    ]
  },
  {
    date: 'Gestern',
    type: 'patch',
    title: 'Animation & Interaktion',
    description: 'Lebendigere Gestaltung des Assistenten.',
    features: [
      'Erweiterte CSS-Animationen für den RAB-i Bot (Winken, Blinzeln)',
      'Verbesserte Timing-Logik für Hinweise'
    ]
  },
  {
    date: 'Vor 2 Tagen',
    type: 'major',
    title: 'AI Health-Check Assistant',
    description: 'Einführung eines intelligenten Assistenten zur Qualitätssicherung.',
    features: [
      'Echtzeit-Analyse der Vollständigkeit (Ampelsystem)',
      'Interaktiver "RAB-i" Bot mit Hilfestellungen',
      'Deep-Link Funktion ("Fix it") zu fehlenden Feldern'
    ]
  },
  {
    date: 'Vor 3 Tagen',
    type: 'minor',
    title: 'Bild- & Risikoanalyse 2.0',
    description: 'Massive Aufwertung des Fotodokumentations-Moduls.',
    features: [
      'Umbenennung zu "Bild- & Risikoanalyse"',
      'Kontext-sensitive KI-Analyse (prüft gegen Katalog)',
      'Detail-Spot-Analyse für Bildausschnitte',
      'Master-Detail Ansicht für bessere Übersicht'
    ]
  },
  {
    date: 'Vor 4 Tagen',
    type: 'major',
    title: 'Adressverzeichnis & Paginierung',
    description: 'Erweiterung der PDF-Struktur um wesentliche Seiten.',
    features: [
      'Neue Seite: Adressverzeichnis für Projektbeteiligte',
      'Smarte Paginierung für den Anhang (keine abgeschnittenen Bilder)',
      'Dynamisches Inhaltsverzeichnis'
    ]
  },
  {
    date: 'Vor 5 Tagen',
    type: 'minor',
    title: 'Layout-Kontrolle',
    description: 'Mehr Kontrolle über das PDF-Erscheinungsbild.',
    features: [
      'Zoom-Regler für Deckblatt-Bild',
      'Manuelle Seitenumbrüche für Katalog & Vorbemerkungen',
      'Zoom-Funktion für die Vorschau'
    ]
  },
  {
    date: 'Vor 1 Woche',
    type: 'patch',
    title: 'Bugfixes',
    description: 'Korrekturen an der Benutzeroberfläche.',
    features: [
      'Fix: Löschen-Button bei Bildern',
      'Fix: Eingabe-Validierung'
    ]
  },
  {
    date: '2023-10-01',
    type: 'major',
    title: 'Initial Release',
    description: 'Veröffentlichung des RAB 32 Generators.',
    features: [
      'Erstellung von Maßnahmenkatalogen',
      'KI-Unterstützung (Gemini)',
      'PDF-Export & Druckvorschau'
    ]
  }
];

export const ChangelogModal: React.FC<ChangelogModalProps> = ({ onClose }) => {
  
  const processedLogs = useMemo(() => {
    let major = 1;
    let minor = 0;
    let patch = 0;

    const reversed = [...RAW_LOGS].reverse();
    
    const withVersions = reversed.map((log, index) => {
      if (index > 0) {
        if (log.type === 'major') {
            major += 1;
            minor = 0;
            patch = 0;
        } else if (log.type === 'minor') {
            minor += 1;
            patch = 0;
        } else {
            patch += 1;
        }
      }
      return {
        ...log,
        version: `${major}.${minor}.${patch}`
      };
    });

    return withVersions.reverse();
  }, []);

  const latestVersion = processedLogs[0].version;

  const getTypeStyles = (type: UpdateType) => {
    switch (type) {
      case 'major': return { 
        bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', 
        icon: <Rocket className="w-4 h-4" />, label: 'Major Update' 
      };
      case 'minor': return { 
        bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', 
        icon: <Sparkles className="w-4 h-4" />, label: 'Feature' 
      };
      case 'patch': return { 
        bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', 
        icon: <Bug className="w-4 h-4" />, label: 'Fix / Patch' 
      };
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4 animate-in fade-in zoom-in-95 duration-200">
      <div className="bg-white w-full max-w-2xl h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col relative">
        
        <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-start">
           <div>
              <div className="flex items-center gap-3 mb-2">
                 <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white p-2 rounded-lg shadow-lg shadow-blue-200">
                    <GitMerge className="w-6 h-6" />
                 </div>
                 <div>
                    <h2 className="text-xl font-black text-slate-800">Updates & Versionen</h2>
                    <p className="text-xs text-slate-500 font-medium">Aktuelle Version: <span className="text-blue-600 font-bold">v{latestVersion}</span></p>
                 </div>
              </div>
           </div>
           <button 
             onClick={onClose}
             className="p-2 bg-white hover:bg-slate-100 rounded-full text-slate-400 hover:text-red-500 transition-colors border border-slate-200"
           >
             <X className="w-5 h-5" />
           </button>
        </div>

        <div className="flex-1 overflow-y-auto p-0 bg-white scroll-smooth relative">
            <div className="absolute left-8 top-0 bottom-0 w-px bg-slate-200 z-0"></div>

            <div className="p-8 relative z-10 space-y-8">
                {processedLogs.map((log, idx) => {
                    const style = getTypeStyles(log.type);
                    const isLatest = idx === 0;

                    return (
                        <div key={idx} className="relative pl-10 group">
                            <div className={`absolute left-0 top-1.5 w-4 h-4 -translate-x-[7px] rounded-full border-2 border-white shadow-sm transition-transform group-hover:scale-125 ${isLatest ? 'bg-blue-600 ring-4 ring-blue-100' : 'bg-slate-300'}`}></div>
                            
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-3">
                                    <span className={`text-xl font-bold tracking-tight ${isLatest ? 'text-slate-900' : 'text-slate-500'}`}>
                                        v{log.version}
                                    </span>
                                    <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border flex items-center gap-1.5 ${style.bg} ${style.text} ${style.border}`}>
                                        {style.icon} {style.label}
                                    </span>
                                    {isLatest && <span className="bg-red-500 text-white text-[11px] font-bold px-2 py-0.5 rounded animate-pulse">NEU</span>}
                                </div>
                                <span className="text-xs font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded">{log.date}</span>
                            </div>

                            <h3 className="text-lg font-bold text-slate-800 mb-1">{log.title}</h3>
                            <p className="text-slate-600 text-sm mb-4 leading-relaxed">{log.description}</p>

                            {log.features.length > 0 && (
                                <ul className="space-y-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
                                    {log.features.map((feat, fIdx) => (
                                        <li key={fIdx} className="text-sm text-slate-700 flex items-start gap-2.5">
                                            <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0"></div>
                                            <span>{feat}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })}

                <div className="relative pl-10 pt-4 opacity-50">
                    <div className="absolute left-0 top-1.5 w-4 h-4 -translate-x-[7px] rounded-full bg-slate-200 border-2 border-white"></div>
                    <p className="text-xs text-slate-400 italic">Projektstart...</p>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
